import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { rateLimiter, RATE_LIMIT_CONFIGS, calculateExponentialBackoff } from '../utils/rateLimiter'
import { validateEmail } from '../utils/validators'
import styles from './LoginPage.module.css'
import { Shield } from '../components/icons/Icons'
//import { clearOldSupabaseCache } from '../utils/authDiagnostics'
//import { testGoogleOAuth, checkRedirectParams } from '../utils/oauthDiagnostics'

export default function LoginPage() {
  const { signInWithEmail, signInWithMicrosoft, signInWithGoogle } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [ssoLoading, setSsoLoading] = useState<'microsoft' | 'google' | null>(null)
  const [loginAttempts, setLoginAttempts] = useState(0)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmedEmail = email.trim()
    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    const rateCheck = rateLimiter.check(trimmedEmail, 'login', RATE_LIMIT_CONFIGS.LOGIN.maxAttempts, RATE_LIMIT_CONFIGS.LOGIN.windowMs)
    if (!rateCheck.allowed) {
      const resetMinutes = Math.ceil((rateCheck.resetTime - Date.now()) / 60000)
      if (rateCheck.blockedUntil) {
        setError(`Account temporarily locked due to multiple failed login attempts. Try again in ${resetMinutes} minute${resetMinutes !== 1 ? 's' : ''}`)
      } else {
        setError(`Too many login attempts. Please try again in ${resetMinutes} minute${resetMinutes !== 1 ? 's' : ''}`)
      }
      return
    }

    if (loginAttempts > 0) {
      const backoffMs = calculateExponentialBackoff(loginAttempts)
      await new Promise(resolve => setTimeout(resolve, backoffMs))
    }

    setLoading(true)
    const { error: err } = await signInWithEmail(trimmedEmail, password)
    setLoading(false)

    if (err) {
      const newAttempts = loginAttempts + 1
      setLoginAttempts(newAttempts)

      if (newAttempts >= RATE_LIMIT_CONFIGS.LOGIN.maxAttempts) {
        rateLimiter.recordFailure(trimmedEmail, 'login', RATE_LIMIT_CONFIGS.LOGIN.windowMs)
        setError('Too many failed login attempts. Your account has been temporarily locked.')
      } else {
        setError(err.message || 'Invalid email or password')
      }
    } else {
      rateLimiter.reset(trimmedEmail, 'login')
      setLoginAttempts(0)
      navigate('/')
    }
  }

  const handleMicrosoft = async () => {
    setSsoLoading('microsoft')
    const { error: err } = await signInWithMicrosoft()
    if (err) { setError(err.message); setSsoLoading(null) }
  }

  const handleGoogle = async () => {
    setSsoLoading('google')
    const { error: err } = await signInWithGoogle()
    if (err) {
      console.error('[LoginPage] Google sign-in error:', err)
      setError(err.message)
      setSsoLoading(null)
    }
  }

  const handleClearCache = () => {
    if (window.confirm('This will clear all cached authentication data and reload the page. Continue?')) {
      Object.keys(localStorage).filter(k => k.includes('auth') || k.includes('token')).forEach(k => localStorage.removeItem(k))
      window.location.reload()
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}><Shield size={28} /></div>
          <div>
            <h1 className={styles.appName}>allocatIQ</h1>
            <p className={styles.tagline}>Resource Forecasting & Utilization</p>
          </div>
        </div>

        <h2 className={styles.heading}>Sign in to your account</h2>

        <div className={styles.ssoGroup}>
          <button
            className={`${styles.ssoBtn} ${styles.microsoft}`}
            onClick={handleMicrosoft}
            disabled={!!ssoLoading}
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            {ssoLoading === 'microsoft' ? 'Redirecting...' : 'Continue with Microsoft'}
          </button>
          <button
            className={`${styles.ssoBtn} ${styles.google}`}
            onClick={handleGoogle}
            disabled={!!ssoLoading}
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {ssoLoading === 'google' ? 'Redirecting...' : 'Continue with Google'}
          </button>
        </div>

        <div className={styles.divider}><span>or sign in with email</span></div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.field}>
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              required
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className={styles.footer}>
          Don't have an account? <Link to="/signup">Create one</Link>
        </p>
        <p className={styles.mfaNote}>
          MFA-protected accounts will be prompted for a second factor after login.
        </p>
        {import.meta.env.DEV && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f3f4f6', borderRadius: '6px', fontSize: '0.875rem' }}>
            <strong>Debug Tools:</strong>
            <button
              onClick={handleClearCache}
              style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.75rem',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
              type="button"
            >
              Clear Auth Cache
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
